import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { ITeleopJoystickAxisConfiguration } from "@formant/core/model/ITeleopJoystickAxisConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { twistDimensions } from "@formant/core/model/twistDimensions";
import { InputElement } from "@formant/ui/component/common/Field";
import { HStack } from "@formant/ui/component/common/HStack";

import { operateStore } from "../../stores";
import { NumberField } from "../common/NumberField";
import { SelectField } from "../common/SelectField";
import { Timer } from "../common/Timer";
import * as styles from "./JoystickAxisConfiguration.scss";

export interface IJoystickAxisConfigurationProps {
    axis: string;
    axisConfig: ITeleopJoystickAxisConfiguration;
    validation?: IValidation;
    joystickIndex: number;
    onEdit: (
        axisConfig: ITeleopJoystickAxisConfiguration,
        axisLabel: string,
    ) => void;
}

@observer
export class JoystickAxisConfiguration extends Component<IJoystickAxisConfigurationProps> {
    @observable
    private listening = false;

    private lastGamepadAxesValues: number[] = [];

    public render() {
        const {
            axis,
            axisConfig: { scale, expo, dimension, gamepadAxis },
            validation,
            joystickIndex,
        } = this.props;

        return (
            <HStack
                hAlign="space-between"
                vAlign="center"
                className={styles["joystick-axis-configuration"]}
            >
                <span className={styles["axis-label"]}>
                    {axis === "x" ? "horizontal" : "vertical"} axis
                </span>
                {this.listening && (
                    <Timer
                        interval={duration.millisecond * 100}
                        onTick={this.onListenForAxisInput}
                    />
                )}
                <SelectField
                    label="Twist dimension"
                    small={true}
                    value={dimension}
                    onChange={this.onChangeTwistDimension}
                >
                    <option key="None" value={undefined}>
                        None
                    </option>
                    {twistDimensions.map(_ => (
                        <option key={_} value={_}>
                            {_}
                        </option>
                    ))}
                </SelectField>
                <NumberField
                    label="GamepadAxis"
                    small={true}
                    numberValue={gamepadAxis}
                    onChangeNumber={this.onChangeGamepadAxis}
                    invalid={
                        Object.keys(validation || {}).filter(_ =>
                            _.includes(
                                `document.teleop.joysticks.${joystickIndex}.${axis}.gamepadAxis`,
                            ),
                        ).length > 0
                    }
                    className={styles.scale}
                    onFocus={this.onStartListening}
                    onBlur={this.onStopListening}
                    data-tooltip="Enter the axis number, or move your joystick to autodetect"
                />
                <NumberField
                    label="Scale"
                    small={true}
                    numberValue={scale}
                    onChangeNumber={this.onChangeScale}
                    invalid={
                        Object.keys(validation || {}).filter(_ =>
                            _.includes(
                                `document.teleop.joysticks.${joystickIndex}.${axis}.scale`,
                            ),
                        ).length > 0
                    }
                    className={styles.scale}
                />
                <NumberField
                    label="Exponent"
                    small={true}
                    numberValue={expo}
                    onChangeNumber={this.onChangeExponent}
                    invalid={
                        Object.keys(validation || {}).filter(_ =>
                            _.includes(
                                `document.teleop.joysticks.${joystickIndex}.${axis}.expo`,
                            ),
                        ).length > 0
                    }
                    className={styles.exponent}
                />
            </HStack>
        );
    }

    private onChangeTwistDimension = (event: ChangeEvent<InputElement>) => {
        const { axisConfig, onEdit, axis } = this.props;
        const dimension = twistDimensions.find(_ => _ === event.target.value);

        axisConfig.dimension = dimension;

        onEdit(axisConfig, axis);
    };

    private onChangeGamepadAxis = (value?: number) => {
        const { axisConfig, onEdit, axis } = this.props;

        axisConfig.gamepadAxis = value;

        onEdit(axisConfig, axis);
    };

    private onChangeScale = (value?: number) => {
        const { axisConfig, onEdit, axis } = this.props;

        axisConfig.scale = value;

        onEdit(axisConfig, axis);
    };

    private onChangeExponent = (value?: number) => {
        const { axisConfig, onEdit, axis } = this.props;

        axisConfig.expo = value;

        onEdit(axisConfig, axis);
    };

    private onStartListening = () => {
        this.listening = true;
    };

    private onStopListening = () => {
        this.listening = false;
    };

    private onListenForAxisInput = () => {
        const gamepad = operateStore.gamepad;

        if (!gamepad?.axes) {
            return;
        }

        if (this.lastGamepadAxesValues.length === 0) {
            this.lastGamepadAxesValues = gamepad.axes.map(_ => _);
            return;
        }

        if (this.lastGamepadAxesValues.length === gamepad.axes.length) {
            const updatedAxes = gamepad.axes
                .map((_, index) => {
                    if (!this.lastGamepadAxesValues[index]) {
                        return undefined;
                    }
                    const diff = Math.abs(
                        this.lastGamepadAxesValues[index] - _,
                    );
                    return diff > 0.2 ? index : undefined;
                })
                .find(_ => _ !== undefined);
            if (updatedAxes !== undefined) {
                this.onChangeGamepadAxis(updatedAxes);
            }
        }

        this.lastGamepadAxesValues = gamepad.axes.map(_ => _);
    };
}
